<script setup lang="ts">
  import { ref, computed, onMounted } from 'vue'
  import { storeToRefs } from 'pinia'
  import { Loader2, Upload, Download } from 'lucide-vue-next'
  import { useReportThemesStore } from '@stores/reportThemes/reportThemes'
  import { useAccountsStore } from '@stores/accounts'
  import { AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
  import { Button } from '@/components/ui/button'
  import { Input } from '@/components/ui/input'
  import { Label } from '@/components/ui/label'
  import { Select } from "@/components/episto-ui/select";

  const store = useReportThemesStore()
  const { currentTheme, localEdits } = storeToRefs(store)

  const accountsStore = useAccountsStore()
  const accountOptions = computed(() =>
    (accountsStore.accounts ?? []).map((a) => ({ value: String(a.id), label: a.name }))
  )

  const uploading = ref(false)
  const uploadError = ref<string | null>(null)
  const pptxFileInput = ref<HTMLInputElement | null>(null)

  onMounted(() => accountsStore.fetch())

  function setEdit(field: string, value: unknown) {
    if (localEdits.value) {
      (localEdits.value as Record<string, unknown>)[field] = value
    }
  }

  async function handlePptxUpload(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (!file || !currentTheme.value) return
    uploading.value = true
    uploadError.value = null
    try {
      await store.uploadPptx(currentTheme.value.token, file)
      // Resets localEdits; the Charts section re-syncs its palettes via its
      // watch on currentTheme.chart_styles.
      store.initEdits()
    } catch (err) {
      uploadError.value = err instanceof Error ? err.message : 'Upload failed'
    } finally {
      uploading.value = false
      ;(e.target as HTMLInputElement).value = ''
    }
  }

  function downloadTemplate() {
    window.open('/api/report_themes/download_template', '_blank')
  }
</script>

<template>
  <AccordionItem value="general">
    <AccordionTrigger class="text-base font-semibold">General</AccordionTrigger>
    <AccordionContent>
      <div class="space-y-4 pt-1">
        <div class="space-y-1.5">
          <Label>Name</Label>
          <Input
            :model-value="localEdits?.name ?? ''"
            class="max-w-xs"
            @update:model-value="(v) => setEdit('name', v)" />
        </div>
        <div class="space-y-1.5">
          <Label>Accounts</Label>
          <Select
            :model-value="(localEdits?.account_ids ?? []).map(String)"
            :options="accountOptions"
            multiple
            filterable
            class="max-w-xs"
            @update:model-value="(v) => setEdit('account_ids', (v as string[]).map(Number))" />
        </div>
        <div class="flex items-center gap-3 pt-1">
          <Button
            variant="outline"
            size="sm"
            @click="downloadTemplate">
            <Download class="w-4 h-4 mr-2" />
            Download template
          </Button>
          <Button
            variant="outline"
            size="sm"
            :disabled="uploading"
            @click="pptxFileInput?.click()">
            <Loader2
              v-if="uploading"
              class="w-4 h-4 mr-2 animate-spin" />
            <Upload
              v-else
              class="w-4 h-4 mr-2" />
            {{ currentTheme?.has_backgrounds ? 'Re-upload PPTX' : 'Upload PPTX' }}
          </Button>
          <span
            v-if="uploadError"
            class="text-xs text-destructive">{{ uploadError }}</span>
        </div>
        <input
          ref="pptxFileInput"
          type="file"
          accept=".pptx"
          class="hidden"
          @change="handlePptxUpload" />
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
